// Driver page sizes (QR_ROLL_1X3_WORDS.md rev 2, MOCKUP_LABEL_PAGE_SIZES.html, approved by Robert 2026-10-30 9:42 AM).
//
// Chrome's print box offers only the paper sizes the Toro Max driver lists. A PDF page that is not one of them
// lands off the label. So every label PDF is built on a driver size: the smallest one with the label's tape
// height that is at least the label's length. The label art never changes. It sits centered on the page, with
// even blank tape at both ends, so a little feed drift has room either way.
//
// The list is the driver on Robert's PC, read from his screen 2026-10-30 9:09 AM. GP names read tape height x
// length, in inches.

export interface DriverPage {
  /** Tape height, inches. */
  height: number;
  /** Length fed, inches. */
  length: number;
}

export const DRIVER_SIZES: readonly DriverPage[] = [
  { height: 0.5, length: 1.5 },
  { height: 1, length: 1 },
  { height: 1, length: 2 },
  { height: 1, length: 3 },
  { height: 1, length: 4 },
  { height: 1, length: 8 },
  { height: 1.4375, length: 3 },
  { height: 2, length: 2 },
  { height: 2, length: 3 },
  { height: 2, length: 4 },
  { height: 2, length: 12 },
  { height: 3, length: 3 },
  { height: 3, length: 4.5 },
  { height: 3, length: 5 },
  { height: 3, length: 5.5 },
  { height: 3, length: 24 },
  { height: 3.5, length: 4 },
  { height: 4, length: 4 },
  { height: 4, length: 6 },
];

/**
 * The driver page for a label of this height and length. When the driver has nothing that fits, the label's own
 * size: the PDF is still right, and the print card names it so Robert can see it.
 */
export function driverPage(height: number, length: number): DriverPage {
  const fits = DRIVER_SIZES.filter((d) => d.height === height && d.length >= length - 1e-9);
  fits.sort((a, b) => a.length - b.length);
  return fits[0] ?? { height, length };
}

/** True when the driver lists this exact size. */
export function onDriverList(p: DriverPage): boolean {
  return DRIVER_SIZES.some((d) => d.height === p.height && d.length === p.length);
}

/** "GP 1 x 3", the name the driver shows. */
export function driverName(p: DriverPage): string {
  return `GP ${p.height} x ${p.length}`;
}

/** Points from the page's left edge to the label's, so the label sits centered on its driver page. */
export function leadPt(labelLength: number, page: DriverPage): number {
  return ((page.length - labelLength) / 2) * 72;
}

/**
 * When the page sizes changed. A Drive PDF sent before this was built on the old page and reads behind, so the
 * card shows Update PDF. Sends carry their time in the name (masterSendName). Set to 9:45 AM Eastern, 2026-10-30, before this build could ship.
 */
export const PAGE_SIZES_SINCE_MS = Date.parse("2026-10-30T13:45:00Z");

/**
 * When the QR roll's start moved to center the label on the tape (Robert, 2026-10-30 11:23 AM: "adjust so its
 * perfect please...centered"). A QR Drive PDF sent before this reads behind. 11:28 AM Eastern, before this build
 * could ship. It comes after PAGE_SIZES_SINCE_MS, so it covers the page size change too.
 */
export const QR_CENTERED_SINCE_MS = Date.parse("2026-10-30T16:15:00Z"); // moved for the 12:13 PM re-center

/** The send's time from its name ("00_QR_Labels__1761832800000.pdf"). 0 when it can't be read. */
export function sendStampMs(sendName: string): number {
  const m = /__(\d{13})\.pdf$/.exec(sendName);
  return m ? Number(m[1]) : 0;
}
